import { BUILD_EVENT, TComponent } from '@core';
import { validations } from '@core/apis';
import * as Events from '@core/events';
import { EEVents } from '@core/constants';
import { extractTargetString, template } from "@core/utils/common";

const DEFAULT_DEBOUNCE_MS = 200;

export const handler = ({
  field,
  form,
  eventReducedSchema: { api },
}: Pick<
  Events.TEventInformation<unknown>,
  'form' | 'field' | 'eventReducedSchema'
>) =>
  api.forEach((apiCall) => {
    const { scope } = field;
    const meetsRunningCondition = validations.run(
      field.data.value,
      apiCall.preConditions || {},
      field.scopedComponent.errorMessages,
      form?.formData?.fields,
    );
    if (
      field.fieldHasError(meetsRunningCondition) ||
      (apiCall.blockRequestWhenInvalid &&
        field.fieldValidationsHaveError(false))
    ) {
      return;
    } else if (apiCall.url.includes(template.BEGIN)) {
      apiCall.url = extractTargetString<string>(
        apiCall.url,
        form.scope,
        field.scope,
      );
    }

    let paramValues = {};
    let url = apiCall.url;

    if (apiCall.fieldValueAsPathParams) {
      for (const param of apiCall.fieldValueAsPathParams) {
        if (Object.prototype.hasOwnProperty.call(form.fields, param)) {
          url = `${url}/${form.fields[param].data.value}`;
        }
      }
    }

    if (apiCall.fieldValueAsParams) {
      for (const key in apiCall.fieldValueAsParams) {
        if (Object.prototype.hasOwnProperty.call(form.fields, key)) {
          const value = apiCall.fieldValueAsParams[key] || key;

          paramValues = {
            ...paramValues,
            [value]: form.fields[key].data.value,
          };
        }
      }

      if (url.includes('?')) {
        const uri = url.split('?');
        const uriParams = uri.at(1).split('&');
        url = uri.at(0);

        if (uriParams.length > 1) {
          for (const uriParam of uriParams) {
            const item = uriParam.split('=');
            paramValues = {
              ...paramValues,
              [item.at(0)]: item.at(1),
            };
          }
        } else {
          const item = uriParams.at(0).split('=');
          paramValues = {
            ...paramValues,
            [item.at(0)]: item.at(1),
          };
        }
      }

      const params = new URLSearchParams(paramValues);
      url = `${url}?${params.toString()}`;
    }

    scope.globalScope = {
      namespace: 'api',
      key: apiCall.scope,
      data: {
        loading: true,
      },
    };
    field.debounce(() => {
      return fetch(url, {
        method: apiCall.method,
        ...(apiCall.body && { body: JSON.stringify(apiCall.body) }),
        headers: apiCall.headers || {
          'Content-type': 'application/json; charset=UTF-8',
        },
      })
        .then((res) => {
          const parsedResponse = res.json();
          if (res.status >= 400) throw parsedResponse;

          return parsedResponse;
        })
        .then((res) => {
          scope.globalScope = {
            namespace: 'api',
            key: apiCall.scope,
            data: {
              loading: false,
              response: res,
              ...(res as any),
            },
          };
        })
        .catch(async (err) => {
          const jsonErr = await err;
          scope.globalScope = {
            namespace: 'api',
            key: apiCall.scope,
            data: {
              loading: false,
              error: true,
              err: jsonErr,
            },
          };
        })
        .finally(() =>
          field.publish(
            BUILD_EVENT(EEVents.AFTER_FIELD_API_CALL, field.component.name),
          ),
        );
    }, apiCall.debounceTime || DEFAULT_DEBOUNCE_MS);
  });

export const events = (component: TComponent) =>
  Object.keys(component.api || {});
